import { observer } from 'mobx-react';
import { useId } from 'react';
import { Dialog, Heading } from 'react-aria-components/Dialog';
import { Modal, ModalOverlay } from 'react-aria-components/Modal';

import Logo from '../../../../b2c/layout/Logo/Logo';
import { ExternalConfiguratorModalTranslation } from '../../../localization/SiteTranslation';
import Button from '../../../shared/components/Button/Button';
import CloseButton from '../../../shared/components/Button/CloseButton';
import TertiaryButton from '../../../shared/components/Button/TertiaryButton';
import ThreeDotsLoader from '../../../shared/components/Loaders/ThreeDotsLoader';
import css from './ExternalConfiguratorModal.css';
import ExternalConfiguratorModalState from './ExternalConfiguratorModalState';

interface ExternalConfiguratorModalProps {
  state: ExternalConfiguratorModalState;
}

const ExternalConfiguratorModal = ({ state }: ExternalConfiguratorModalProps) => {
  const { confirmationModal, translation } = state;

  return (
    <ModalOverlay
      className={css.overlay}
      isOpen={state.visible}
      onOpenChange={(open) => !open && confirmationModal.open()}
    >
      <Modal className={css.modal}>
        <Dialog className={css.dialog} aria-label={translation.title}>
          <header className={css.header}>
            <div className={css.headerInner}>
              <Logo />
              <CloseButton onClick={() => confirmationModal.open()}>{translation.closeButton}</CloseButton>
            </div>
          </header>
          {state.isLoading && (
            <div className={css.loader} role="status">
              <ThreeDotsLoader />
              {state.selectedLayout ? translation.fromLayoutLoadingText : translation.fromScratchLoadingText}
            </div>
          )}
          <iframe
            className={css.iframe}
            src={state.configuratorUrl}
            title={translation.title}
            onLoad={state.setLoaded}
          />
          <ConfirmationModal
            isOpen={confirmationModal.visible}
            translation={translation.confirmation}
            onOpenChange={confirmationModal.toggle}
            onConfirm={state.close}
          />
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};

export default observer(ExternalConfiguratorModal);

interface ConfirmationModalProps {
  isOpen: boolean;
  translation: ExternalConfiguratorModalTranslation['confirmation'];
  onConfirm: () => void;
  onOpenChange: (isOpen: boolean) => void;
}

const ConfirmationModal = ({ isOpen, onConfirm, onOpenChange, translation }: ConfirmationModalProps) => {
  const descriptionId = useId();

  return (
    <ModalOverlay className={css.confirmationOverlay} isOpen={isOpen} onOpenChange={onOpenChange} isDismissable>
      <Modal className={css.confirmationModal}>
        <Dialog className={css.confirmationDialog} role="alertdialog" aria-describedby={descriptionId}>
          <Heading level={2} className={css.confirmationTitle} slot="title">
            {translation.title}
          </Heading>
          <p className={css.confirmationDescription} id={descriptionId}>
            {translation.description}
          </p>
          <div className={css.confirmationActions}>
            <TertiaryButton type="button" className={css.confirmationCloseButton} onClick={onConfirm}>
              {translation.closeButton}
            </TertiaryButton>
            {/* Focused first as the least destructive action */}
            <Button type="button" onClick={() => onOpenChange(false)} autoFocus>
              {translation.continueButton}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
